import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type LocationCoords = {
  lat: number;
  lng: number;
};

export type SetCoordsPayload = LocationCoords & {
  locationLabel?: string;
};

export type LocationSearchState = {
  coords: LocationCoords | null;
  locationLabel: string | null;
  sortByDistance: boolean;
  selectedCommunities: string[];
  nationwideTotal: number;
  communities: string[];
  hasSearched: boolean;
  searchResetVersion: number;
};

const initialState: LocationSearchState = {
  coords: null,
  locationLabel: null,
  sortByDistance: false,
  selectedCommunities: [],
  nationwideTotal: 0,
  communities: [],
  hasSearched: false,
  searchResetVersion: 0,
};

const areSameCommunities = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const locationSearchSlice = createSlice({
  name: 'locationSearch',
  initialState,
  reducers: {
    setCoords: (state, action: PayloadAction<SetCoordsPayload>) => {
      state.coords = { lat: action.payload.lat, lng: action.payload.lng };
      state.locationLabel = action.payload.locationLabel?.trim() || null;
      state.sortByDistance = true;
      state.hasSearched = true;
    },
    toggleCommunity: (state, action: PayloadAction<string>) => {
      const next = action.payload;
      const index = state.selectedCommunities.indexOf(next);
      if (index >= 0) {
        state.selectedCommunities.splice(index, 1);
        return;
      }
      state.selectedCommunities.push(next);
    },
    clearCommunity: (state) => {
      state.selectedCommunities = [];
    },
    setNationwideTotal: (state, action: PayloadAction<number>) => {
      if (state.nationwideTotal > 0 || action.payload <= 0) {
        return;
      }
      state.nationwideTotal = action.payload;
    },
    setCommunities: (state, action: PayloadAction<string[]>) => {
      if (state.selectedCommunities.length > 0) {
        return;
      }
      if (areSameCommunities(state.communities, action.payload)) {
        return;
      }
      state.communities = action.payload;
    },
    resetSearch: (state) => ({
      ...initialState,
      nationwideTotal: state.nationwideTotal,
      searchResetVersion: state.searchResetVersion + 1,
    }),
  },
});

export const {
  setCoords,
  toggleCommunity,
  clearCommunity,
  setNationwideTotal,
  setCommunities,
  resetSearch,
} = locationSearchSlice.actions;

export default locationSearchSlice.reducer;
